import React, {useEffect, useState} from 'react';
import {Badge, Button} from "reactstrap";
import {Auth} from "../../services/api/Auth";
import {apiErrorRaised} from "../../context/api_error/ApiErrorActions";
import {useApiErrorDispatch} from "../../context/api_error/ApiErrorIndex";
import {closeModal, openModal} from "../../context/modal/ModalActions";
import {useModalDispatch} from "../../context/modal/ModalIndex";
import {Link} from "react-router-dom";

const ManageUserItem = ({data, setDeletedUserAccess, reloadUsers}) => {
  const apiErrorDispatch = useApiErrorDispatch()
  const modalDispatch = useModalDispatch()
  const [user, setUser] = useState([])
  const [linkResent, setLinkResent] = useState(false)
  const [isStatusToggling, setIsStatusToggling] = useState(false)

  useEffect(() => {
    setUser(formatUserData(data))
  }, [data])

  // Helper function to get appropriate badge color based on status
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 1:
        return 'success';
      case 3:
        return 'danger';
      case 0:
        return 'warning';
      case 2:
        return 'dark';
      case 4:
        return 'info';
      case 5:
        return 'primary';
      default:
        return 'secondary';
    }
  };

  // Get status text based on status code
  const getStatusText = (status) => {
    switch (status) {
      case 1:
        return 'Active';
      case 0:
        return 'Disabled';
      case 2:
        return 'Deleted';
      case 3:
        return 'Locked';
      case 4:
        return 'Invited';
      case 5:
        return 'Needs Confirmation';
      default:
        return 'Unknown';
    }
  };

  // Helper function to get appropriate badge color based on role
  const getRoleBadgeColor = (role) => {
    switch (role?.toLowerCase()) {
      case 'user':
        return 'warning';
      case 'admin':
        return 'primary';
      case 'collaborator':
        return 'success';
      case 'viewer':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  // Handle delete user
  const handleDeleteUser = () => {
    openModal(modalDispatch, {
      title: 'Delete User',
      message: 'Are you sure you want to delete this user? This action cannot be undone.',
      onConfirm: () => {
        Auth.deleteUser(user.id).then((r) => {
          if (r?.status < 400) {
            setDeletedUserAccess(true);
            // Reload users after successful deletion
            if (reloadUsers) {
              reloadUsers();
            }
          } else {
            apiErrorRaised(apiErrorDispatch, r?.response)
          }
          closeModal(modalDispatch)
        });
      },
      onCancel: () => closeModal(modalDispatch),
      onBackdropClick: () => closeModal(modalDispatch),
      buttonColor: 'danger',
      buttonText: 'Delete',
      buttonIcon: 'fa-trash'
    })
  }

  // Handle resend invitation
  const handleResendLink = () => {
    openModal(modalDispatch, {
      title: 'Resend Invitation',
      message: 'Do you want to resend the invitation email to this user?',
      onConfirm: () => {
        Auth.resendInvitation(user.id).then((r) => {
          if (r?.status < 400) {
            setLinkResent(true)
            // Reload users after successful resend
            if (reloadUsers) {
              reloadUsers();
            }
            // Show success message
            openModal(modalDispatch, {
              title: 'Success',
              message: 'Invitation has been resent successfully.',
              onConfirm: () => closeModal(modalDispatch),
              onCancel: () => closeModal(modalDispatch),
              onBackdropClick: () => closeModal(modalDispatch),
              buttonColor: 'success',
              buttonText: 'OK',
            })
          } else {
            apiErrorRaised(apiErrorDispatch, r?.response)
          }
          closeModal(modalDispatch)
        });
      },
      onCancel: () => closeModal(modalDispatch),
      onBackdropClick: () => closeModal(modalDispatch),
      buttonColor: 'info',
      buttonText: 'Resend',
      buttonIcon: 'fa-envelope'
    })
  }

  // Should show resend button for invited or needs confirmation users
  const shouldShowResendButton = () => {
    return user.status === 4 || user.status === 5; // Invited or Needs Confirmation
  }

  // Handle toggle user status (activate/deactivate)
  const handleToggleStatus = (e) => {
    const {checked} = e.target;
    setIsStatusToggling(true);

    if (checked) {
      // Activate user
      Auth.activateUser(user.id).then((r) => {
        setIsStatusToggling(false);
        if (r?.status < 400) {
          setUser({...user, status: 1}); // Set status to active (1)
        } else {
          apiErrorRaised(apiErrorDispatch, r?.response);
        }
      });
    } else {
      // Deactivate user
      Auth.deactivateUser(user.id).then((r) => {
        setIsStatusToggling(false);
        if (r?.status < 400) {
          setUser({...user, status: 0}); // Set status to disabled (0)
        } else {
          apiErrorRaised(apiErrorDispatch, r?.response);
        }
      });
    }
  }

  // Should show status toggle only for active (1) or disabled (0) users
  const canToggleStatus = () => {
    return user.status === 1 || user.status === 0;
  }

  // Format date or show placeholder if null
  const formatUserData = (user) => {
    if (user.login.last_login === null) {
      user = {
        ...user,
        last_login: 'Never',
      }
    } else {
      let date = new Date(user.login.last_login);
      user = {
        ...user,
        last_login: date.toLocaleString(),
      }
    }
    if (user.login.last_access === null) {
      user = {
        ...user,
        last_access: 'Never',
      }
    } else {
      let date = new Date(user.login.last_access);
      user = {
        ...user,
        last_access: date.toLocaleString(),
      }
    }

    // Extract full name from person data if available
    const firstName = user.person?.full_name?.first_name || '';
    const lastName = user.person?.full_name?.last_name || '';
    user = {
      ...user,
      fullname: firstName && lastName ? `${firstName} ${lastName}`.trim() : (firstName || lastName || '-')
    }

    return user
  };

  return (
    <>
      <tr>
        <td>
          {user.id}
        </td>
        <td>
          {user.email}
        </td>
        <td>
          {user.fullname || "-"}
        </td>
        <td>
          <Badge color={getRoleBadgeColor(user.role)} pill className={'badge-lg'}>
            {user.role}
          </Badge>
        </td>
        <td>
          <Badge color={getStatusBadgeColor(user.status)} pill className={'badge-lg'}>
            {getStatusText(user.status)}
          </Badge>
        </td>
        <td>
          {user.last_login}
        </td>
        <td>
          {user.last_access}
        </td>
        <td className="">
          {canToggleStatus() && (
            <div className="me-3">
              <label className="custom-toggle me-1">
                <input
                  type="checkbox"
                  checked={user.status === 1}
                  onChange={handleToggleStatus}
                  disabled={isStatusToggling}
                />
                <span className="custom-toggle-slider rounded-circle"/>
              </label>
            </div>
          )}
        </td>
        <td>
          <Link to={`/admin/users/${user.id}/access`}>
            <Button
              color={'primary'}
              className={'btn-icon-only me-2'}
              title="View All Accesses"
              outline={true}
            >
              <span className="fa-solid fa-building-user"/>
            </Button>
          </Link>
          <Button
            color={'danger'}
            className={'btn-icon-only'}
            onClick={handleDeleteUser}
            title="Delete User"
            outline={true}
          >
            <span className="fa-solid fa-trash"/>
          </Button>
          {shouldShowResendButton() && (
            <Button
              color={'info'}
              className={'btn-icon-only '}
              onClick={handleResendLink}
              title="Resend Link"
              outline={true}
            >
              <span className="fa-solid fa-envelope"/>
            </Button>
          )}
        </td>
      </tr>
    </>
  )
}

export default ManageUserItem;
